import { useCallback } from 'react';

import { retry } from '@confluence/retry';

// This hook will run `forceRefetchContent`. If the response from forceRefetchContent's content query is outdated because
//   subtype is still 'live' instead of null like we would expect a published page to be, then this hook will
//   re-run forceRefetchContent per our maxRetryCount & retryDelayMs args.
// This should resolve a rare usePublish race condition where sometimes publishing a live doc will not have its subtype change
//   propagated quickly enough to the subtype returned by forceRefetchContent's ContentAndHeaderQuery.
// forceRefetchContent is responsible for ensuring the page the user is viewing is updated to render as a view page instead of a live doc,
//   by updating the contentId's subtype in the Apollo cache. This bug prevents the user from transitioning smoothly from a live doc to the view page
//   when they publish. They would instead be stuck on the live doc with a "successfully published" flag, and would have to refresh to see view page.
export const useForceRefetchPublishedLiveDoc = ({
	forceRefetchContent,
	maxRetryCount,
	retryDelayMs,
}) => {
	return useCallback(async () => {
		let attempts = 0;
		let isResponseInvalid = false;
		let isResponseOutdated = false;
		const forceRefetchAction = async () => {
			const { contentAndHeaderQueryResponse } = await forceRefetchContent();

			attempts++;
			isResponseInvalid = !Boolean(contentAndHeaderQueryResponse?.data?.content?.nodes?.length);
			isResponseOutdated =
				contentAndHeaderQueryResponse?.data?.content?.nodes?.[0]?.subType === 'live';

			// Throw an error so we retry
			if (isResponseInvalid || isResponseOutdated) {
				throw new Error('forceRefetchPublishedLiveDoc invalid/outdated');
			}
		};

		try {
			await retry(forceRefetchAction, { maxAttempts: maxRetryCount, timeout: retryDelayMs });
		} catch {
			// retry function throws an error if there are no retry attempts remaining
		}

		return { retryCount: attempts - 1, isResponseInvalid, isResponseOutdated };
	}, [forceRefetchContent, maxRetryCount, retryDelayMs]);
};
